import {
  Carousel,
  CarouselContent,
  CarouselIndicator,
  CarouselItem,
  type CarouselApi,
} from '@lastbrand/web-ui/carousel'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { ReactNode, useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'

import type { ProductCardImage } from '@src/components/product-card/product-card.types'
import { Image } from '@src/quince-components/src'

export interface CardImageCarouselProps {
  /** Full per-variant image list for the selected variant (already gender-filtered by the caller). */
  images: ProductCardImage[]
  /** Resolves the alt text for an image (shared with {@link ImageTile} for parity). */
  resolveAlt: (image: ProductCardImage) => string
  /** Loading for the FIRST slide only — inherited from the grid's first-row `eagerLoadImage`. */
  imageLoading?: 'eager' | 'lazy'
  imageQuality?: number
  imageMaxWidth?: number
  /** Wraps each slide so a tap navigates to the PDP (reuses the card's `ProductCardLink`). */
  renderSlideLink: (node: ReactNode, image: ProductCardImage, index: number) => ReactNode
  /** Fired when the user settles on a NEW slide (not the initial one) — drives the gallery-viewed event. */
  onSlideView?: (index: number) => void
  ariaLabel: string
}

/** Pointer travel (px) past which a click is treated as a swipe, not a tap → no PDP navigation. */
const DRAG_THRESHOLD_PX = 10

const rootCss = css.raw({ width: 'full', height: 'full' })
const contentCss = css.raw({ height: 'full' })
// Each slide fills the fixed-aspect tile box, so the carousel introduces no layout shift vs the single-image card.
// `& a` makes the reused ProductCardLink anchor fill the slide (same trick as the card's tap overlay), so a
// tap anywhere on the image navigates to the PDP.
const itemCss = css.raw({
  position: 'relative',
  minWidth: 0,
  height: 'full',
  '& a': { position: 'absolute', inset: 0, display: 'block' },
})
const slideImageClass = css({ position: 'absolute', inset: 0, width: '100%', height: '100%' })
const indicatorCss = css.raw({
  position: 'absolute',
  bottom: 'space-8',
  left: 0,
  right: 0,
  zIndex: 1,
  display: 'flex',
  justifyContent: 'center',
})

/**
 * Mobile-only swipeable image carousel rendered INSIDE the product card (CLP + Search, `show` bucket).
 *
 * Reuses the Embla-based DS {@link Carousel}. The card's clickable surface is moved onto each slide
 * (`renderSlideLink`) so a tap opens the PDP; a swipe must not, so we suppress the click that follows a
 * drag by measuring pointer travel (Embla v8 dropped `clickAllowed`). First slide loads with the
 * inherited `imageLoading` (eager only on the grid's first row).
 *
 * Non-first slides are NOT mounted with a `src` until the user first touches/navigates the carousel
 * (`activated`). `loading="lazy"` alone does not hold the LCP budget: every slide of an above-the-fold
 * card sits at that card's vertical position (just offset horizontally), so Chrome fetches the lazy
 * slides during initial load anyway — tripling image payload and starving the LCP image of bandwidth on
 * throttled mobile. Deferring the `src` until first interaction keeps the initial load identical to the
 * single-image card; the rest stream in (still `lazy`) the moment the user engages.
 */
export function CardImageCarousel({
  images,
  resolveAlt,
  imageLoading,
  imageQuality,
  imageMaxWidth,
  renderSlideLink,
  onSlideView,
  ariaLabel,
}: CardImageCarouselProps) {
  const [emblaApi, setEmblaApi] = useState<CarouselApi>()
  // Non-first slides withhold their image `src` until the user first engages the carousel — see the
  // component doc. Flipped by the first pointer-down (touch/swipe) or any programmatic slide change
  // (indicator tap, keyboard nav). One-way latch: once true, all slides keep their src.
  const [activated, setActivated] = useState(false)
  const lastIndexRef = useRef(0)
  // Distinguish a tap (navigate) from a swipe (don't): Embla v8 dropped `clickAllowed`, so we measure
  // pointer travel ourselves. A click that follows movement past the threshold is treated as a drag.
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null)
  const draggedRef = useRef(false)

  // Register the Embla listeners in an effect with explicit teardown. The DS `Carousel` re-invokes
  // `setEmblaApi` whenever its `setEmblaApi` prop identity changes, so registering inside that callback
  // (as before) stacked a fresh `select`/`reInit` pair on the SAME api every time `onSlideView`'s
  // identity changed (variant select, tag refresh) — firing `productGalleryViewed` 2×, 3×… Keying the
  // effect on the api + `onSlideView` and returning `off(...)` guarantees exactly one live listener pair.
  useEffect(() => {
    if (!emblaApi) return
    const onSelect = () => {
      const index = emblaApi.selectedScrollSnap()
      if (index === lastIndexRef.current) return
      lastIndexRef.current = index
      setActivated(true)
      onSlideView?.(index)
    }
    // `reInit` fires whenever Embla re-initialises — a slide-list change AND a container resize
    // (`watchResize` defaults to true: orientation change, browser-chrome show/hide). Embla preserves
    // the selected index across a resize-reInit, so we only resync the tracking baseline here; never
    // emit, and never force a reset (that would snap a mid-swipe shopper back on a plain resize). The
    // reset-to-first on an actual variant change is handled by the variant-change effect below.
    const onReInit = () => {
      lastIndexRef.current = emblaApi.selectedScrollSnap()
    }
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onReInit)
    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onReInit)
    }
  }, [emblaApi, onSlideView])

  // Reset to the first image ONLY on an actual variant/color change — keyed on the first image id, the
  // same signal the legacy ProductImageSection uses. A resize-driven reInit does not change the image
  // list, so this never fires for it (that asymmetry was the reset-on-resize bug). Set the baseline to 0
  // BEFORE scrolling so the resulting `select` is treated as the new baseline, not a swipe (no gallery
  // event). Guarded by a ref so it does not run on the initial mount (already on slide 0).
  const firstImageId = images[0]?.id
  const prevFirstImageIdRef = useRef(firstImageId)
  useEffect(() => {
    if (!emblaApi || prevFirstImageIdRef.current === firstImageId) return
    prevFirstImageIdRef.current = firstImageId
    lastIndexRef.current = 0
    emblaApi.scrollTo(0, true)
  }, [emblaApi, firstImageId])

  const handlePointerDownCapture = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    pointerStartRef.current = { x: event.clientX, y: event.clientY }
    draggedRef.current = false
    setActivated(true)
  }, [])

  const handlePointerMoveCapture = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const start = pointerStartRef.current
    if (!start) return
    if (
      Math.abs(event.clientX - start.x) > DRAG_THRESHOLD_PX ||
      Math.abs(event.clientY - start.y) > DRAG_THRESHOLD_PX
    ) {
      draggedRef.current = true
    }
  }, [])

  // A drag ends in a synthetic click on the slide link; block that click so swiping never navigates.
  const handleClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (draggedRef.current) {
      event.preventDefault()
      event.stopPropagation()
      draggedRef.current = false
    }
  }, [])

  return (
    <Carousel
      aria-label={ariaLabel}
      opts={{ align: 'start', containScroll: 'trimSnaps' }}
      setEmblaApi={setEmblaApi}
      css={rootCss}
      onPointerDownCapture={handlePointerDownCapture}
      onPointerMoveCapture={handlePointerMoveCapture}
      onClickCapture={handleClickCapture}
    >
      <CarouselContent css={contentCss} viewportCss={contentCss}>
        {images.map((image, index) => {
          // Slide 0 always loads; the rest are NOT mounted as <Image> until `activated`, so they make no
          // network request during initial page load and never starve the LCP image. Passing an empty
          // url to <Image> is NOT enough — it still emits a `src` (the page URL with image query params)
          // and fetches it. So deferred slides render an empty box that fills the slide (keeping swipe
          // snaps + the PDP tap target intact); the real image mounts on first interaction.
          const shouldLoad = index === 0 || activated
          return (
            <CarouselItem key={image.id} css={itemCss}>
              {renderSlideLink(
                shouldLoad ? (
                  <Image
                    url={image.url}
                    alt={resolveAlt(image)}
                    title=""
                    width={image.width}
                    height={image.height}
                    loading={index === 0 ? imageLoading : 'lazy'}
                    quality={imageQuality}
                    maxWidth={imageMaxWidth}
                    className={slideImageClass}
                  />
                ) : (
                  // `role="img"` + `aria-label` keep the wrapping PDP link named (same accessible name as
                  // the real image) so keyboard / screen-reader users never tab to an unnamed link before
                  // activation, while still mounting no <Image>/src (no network request).
                  <div role="img" aria-label={resolveAlt(image)} className={slideImageClass} />
                ),
                image,
                index,
              )}
            </CarouselItem>
          )
        })}
      </CarouselContent>
      <CarouselIndicator css={indicatorCss} />
    </Carousel>
  )
}
